<template>
	<span class="cpty_pill"
		:class="[`cpty_pill_${slug}`, `cpty_pill_${size}`]">
		<span v-if="icon" class="cpty_pill_icon" aria-hidden="true">{{ icon }}</span>
		{{ label }}
	</span>
</template>

<script>
// Pill transversal del TIPO de contraparte (Proveedor, Cliente, Trabajador,
// Honorario, Internacional, Finiquito, Recibo). Fuente única de color + ícono +
// label, para que la clasificación, los rankings de los modales de análisis y
// el detalle de cliente se lean igual en todo el sistema. El color sale de los
// tokens `--counterpart-<tipo>-*` (`:root`, _report_themes.scss); el ícono, del
// mapa de abajo. Genérico: sin Vuex, solo props.
const TYPE_ICONS = Object.freeze({
	proveedor: "💼",
	cliente: "🤝",
	trabajador: "🧑‍💻",
	honorario: "🕒",
	internacional: "🌍",
	finiquito: "📄",
	recibo: "🧾",
	nota_de_venta: "💵",
	"clientes-v-cv": "🛒",
});

// Overrides de display: el `type` que llega del backend es el VALOR INTERNO
// (matchea UserCounterpartType.counterpart_types en BD y los values de los
// filtros) y NO se renombra; acá se traduce sólo lo que se muestra.
const TYPE_LABELS = Object.freeze({
	nota_de_venta: "Nota de Ingreso",
	// Ventas/Costos Devengados: distinto de 'Cliente' (SII) — mismo display
	// humano, valor interno y paleta propios.
	"clientes-v-cv": "Clientes",
});

export default {
	name: "CounterpartTypePill",
	props: {
		// Etiqueta del tipo tal como viene del backend ("Proveedor", etc.).
		type: { type: String, required: true },
		// Tamaño de la pill según el contexto: "sm" (12px, tablas/rankings —
		// default), "lg" (14px, header del drawer), "xl" (20px, modal de
		// clasificación). Sólo cambia font/padding/radius; color e ícono iguales.
		size: {
			type: String,
			default: "sm",
			validator: (v) => ["sm", "lg", "xl"].includes(v),
		},
	},
	computed: {
		// Slug normalizado → clase de color + key del ícono. Tipo desconocido →
		// sin ícono y sin color de tipo (cae al shape base, degradación segura).
		slug() {
			return String(this.type || "").trim().toLowerCase().replace(/\s+/g, "_");
		},
		icon() {
			return TYPE_ICONS[this.slug] || "";
		},
		label() {
			return TYPE_LABELS[this.slug] || this.type;
		},
	},
};
</script>

<style lang="scss" scoped>
.cpty_pill {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 2px 8px;
	border-radius: 999px;
	border: 1px solid transparent;
	font-size: 12px;
	font-weight: 500;
	line-height: 18px;
	white-space: nowrap;
}

.cpty_pill_icon {
	font-size: 0.95em;
}

// Tamaños por contexto. "sm" usa los valores base de arriba; lg/xl sólo
// ajustan font/padding/radius (replican el tamaño que cada vista ya tenía).
.cpty_pill_lg {
	font-size: 14px;
	padding: 2px 10px;
	border-radius: 12px;
}

.cpty_pill_xl {
	font-size: 20px;
	padding: 4px 12px;
	border-radius: 16px;
}

// Color por tipo desde los tokens transversales (:root). Cada tipo, su paleta.
.cpty_pill_proveedor { background: var(--counterpart-proveedor-bg); color: var(--counterpart-proveedor-text); border-color: var(--counterpart-proveedor-border); }
.cpty_pill_cliente { background: var(--counterpart-cliente-bg); color: var(--counterpart-cliente-text); border-color: var(--counterpart-cliente-border); }
.cpty_pill_trabajador { background: var(--counterpart-trabajador-bg); color: var(--counterpart-trabajador-text); border-color: var(--counterpart-trabajador-border); }
.cpty_pill_honorario { background: var(--counterpart-honorario-bg); color: var(--counterpart-honorario-text); border-color: var(--counterpart-honorario-border); }
.cpty_pill_internacional { background: var(--counterpart-internacional-bg); color: var(--counterpart-internacional-text); border-color: var(--counterpart-internacional-border); }
.cpty_pill_finiquito { background: var(--counterpart-finiquito-bg); color: var(--counterpart-finiquito-text); border-color: var(--counterpart-finiquito-border); }
.cpty_pill_recibo { background: var(--counterpart-recibo-bg); color: var(--counterpart-recibo-text); border-color: var(--counterpart-recibo-border); }
.cpty_pill_nota_de_venta { background: var(--counterpart-nota-venta-bg); color: var(--counterpart-nota-venta-text); border-color: var(--counterpart-nota-venta-border); }
.cpty_pill_clientes-v-cv { background: var(--counterpart-clientes-v-cv-bg); color: var(--counterpart-clientes-v-cv-text); border-color: var(--counterpart-clientes-v-cv-border); }
</style>
